import { MediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { Tagline } from '@euler/app/components/Tagline';
import {
  buildTimeline,
  TimelineCell,
  useTaskTimeline,
  useTaskTimelineModels,
} from '@euler/app/flows/task/functions/timeline';
import { usePressTaskTimelineSectionHeader } from '@euler/app/flows/task/functions/usePressTaskTimelineSectionHeader';
import { Colors, StatusColors } from '@euler/components/Colors';
import { EmptyView } from '@euler/components/EmptyView';
import { useContainerLayout } from '@euler/components/layout/LayoutProvider';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { safeMarkDevFlag } from '@euler/utils';
import { formatDateTime } from '@euler/utils/formatters';
import { FC, memo, useMemo } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  ScrollView,
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Svg, { Circle, Line } from 'react-native-svg';

const kSectionLineHeight = 44;
const kSectionConnectorWidth = 18;
const kSectionConnectorLeftMargin = 0;
const kSectionConnectorRightMargin = 8;
const kSectionConnectorRadius = 5;

const kEventConnectorWidth = 12;
const kEventConnectorLineHeight = 30;
const kEventConnectorLeftMargin = 8;
const kEventConnectorRightMargin = 8;
const kEventConnectorRadius = 3;

const kTechnicianNameWidth = 50;
const kCellHorizontalPadding = 15;
const kConnectorLineDotSpace = 3;

const kLogConnector = safeMarkDevFlag(false);

const Connector = memo(
  ({
    first,
    last,
    width,
    lineHeight,
    radius,
    spacer = kConnectorLineDotSpace,
    strokeColor = '#ddd',
    fillColor = StatusColors.Info,
    lineOnly,
    style,
    hint,
    lineHidden,
    ...props
  }: {
    first?: boolean;
    last?: boolean;
    width: number;
    height: number;
    lineHeight: number;
    radius: number;
    spacer?: number;
    strokeColor?: string;
    fillColor?: string;
    lineOnly?: boolean;
    style?: StyleProp<ViewStyle>;
    hint?: string;
    lineHidden?: boolean;
  }) => {
    const { rpxRounded: rpx } = useResponsiveContext();
    const height = Math.ceil(props.height ?? rpx(24));
    const dy = spacer;
    const cx = width / 2;
    const cy = lineHeight / 2;
    kLogConnector &&
      console.log(`[${hint ?? '?'}] connector props: `, {
        lineOnly,
        width,
        height,
        radius,
        dy,
        cx,
        cy,
      });
    return (
      <Svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        css={`
          flex-grow: 0;
          flex-shrink: 0;
        `}
        style={style}
      >
        {!first && !lineOnly && (
          <Line
            x1={cx}
            y1={0}
            x2={cx}
            y2={cy - radius - dy}
            stroke={strokeColor}
          />
        )}
        {!last && !lineOnly && (
          <Line
            x1={cx}
            y1={cy + radius + dy}
            x2={cx}
            y2={height}
            stroke={strokeColor}
          />
        )}
        {!lineOnly && <Circle cx={cx} cy={cy} r={radius} fill={fillColor} />}
        {lineOnly && (
          <Line
            x1={cx}
            y1={0}
            x2={cx}
            y2={height}
            stroke={lineHidden ? 'transparent' : strokeColor}
          />
        )}
      </Svg>
    );
  },
);

const Cell = memo(
  ({
    model,
    width,
    headerTextStyle,
  }: {
    model: TimelineCell;
    width: number;
    headerTextStyle?: StyleProp<TextStyle>;
  }) => {
    const { rpxRounded: rpx } = useResponsiveContext();
    if (model.type === 'section-header') {
      return (
        <View
          css={`
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: 0 ${rpx(kCellHorizontalPadding)}px;
            height: ${rpx(kSectionLineHeight)}px;
            overflow: hidden;
          `}
        >
          <Connector
            first={model.first}
            last={model.last}
            width={rpx(kSectionConnectorWidth)}
            height={rpx(kSectionLineHeight)}
            lineHeight={rpx(kSectionLineHeight)}
            radius={rpx(kSectionConnectorRadius)}
            css={`
              margin-left: ${rpx(kSectionConnectorLeftMargin)}px;
              margin-right: ${rpx(kSectionConnectorRightMargin)}px;
            `}
          />
          <View
            css={`
              flex: 1;
              border-bottom-width: 1px;
              border-bottom-color: #ddd;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
              height: ${rpx(kSectionLineHeight)}px;
            `}
          >
            <Text
              css={`
                flex: 1;
                font-family: ${FontFamily.NotoSans.Medium};
                font-size: ${rpx(18)}px;
                line-height: ${rpx(kSectionLineHeight)}px;
              `}
              style={headerTextStyle}
            >
              {model.title}
            </Text>
            {model.onPressLink ? (
              <TouchableOpacity
                onPress={() => model.onPressLink?.(model.sectionType)}
              >
                <Text
                  css={`
                    color: ${Colors.Green2};
                    font-family: ${FontFamily.NotoSans.Light};
                    font-size: ${rpx(12)}px;
                  `}
                  style={model.linkTextStyle}
                >
                  {model.linkText ?? '查看报告'}
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      );
    } else {
      const mediaCellSpacer = rpx(6);
      const mediaListWidth =
        width -
        rpx(
          kSectionConnectorWidth +
            kSectionConnectorLeftMargin +
            kSectionConnectorRightMargin,
        ) -
        rpx(kTechnicianNameWidth) -
        rpx(
          kEventConnectorWidth +
            kEventConnectorLeftMargin +
            kEventConnectorRightMargin,
        ) -
        rpx(kCellHorizontalPadding) * 2 -
        mediaCellSpacer -
        rpx(20);

      const numberOfMediasPerRow = 4;
      const numberOfMediaRows = Math.ceil((model.medias?.length ?? 0) / 4);
      const mediaSize =
        (mediaListWidth - (numberOfMediasPerRow - 1) * mediaCellSpacer) /
        numberOfMediasPerRow;
      const mediaListHeight = numberOfMediaRows * (mediaSize + 4);
      const height =
        /* title row */ rpx(kEventConnectorLineHeight) +
        /* tag lines */ (model.taglines?.length ?? 0) * rpx(18 + 4) +
        /* timestamps */ rpx(16) +
        mediaListHeight +
        rpx(10);

      return (
        <View
          css={`
            flex-direction: row;
            align-items: stretch;
            justify-content: flex-start;
            padding: 0 ${rpx(kCellHorizontalPadding)}px;
            overflow: hidden;
            height: ${height}px;
            //background-color: gray;
          `}
        >
          <Connector
            lineOnly
            lineHidden={model.lastSection}
            width={rpx(kSectionConnectorWidth)}
            height={height}
            lineHeight={rpx(kEventConnectorLineHeight)}
            radius={0}
            css={`
              margin-left: ${rpx(kSectionConnectorLeftMargin)}px;
              margin-right: ${rpx(kSectionConnectorRightMargin)}px;
            `}
          />
          <View>
            <Text
              numberOfLines={1}
              css={`
                width: ${rpx(kTechnicianNameWidth)}px;
                flex-grow: 0;
                flex-shrink: 0;
                line-height: ${rpx(kEventConnectorLineHeight)}px;
                font-family: ${FontFamily.NotoSans.Light};
                font-size: ${rpx(12)}px;
                color: ${Colors.Gray2};
              `}
            >
              {model.technicianName}
            </Text>
          </View>
          <Connector
            first={model.first}
            last={model.last}
            width={rpx(kEventConnectorWidth)}
            height={height}
            lineHeight={rpx(kEventConnectorLineHeight)}
            radius={rpx(kEventConnectorRadius)}
            css={`
              margin-left: ${rpx(kEventConnectorLeftMargin)}px;
              margin-right: ${rpx(kEventConnectorRightMargin)}px;
            `}
          />
          <View
            css={`
              flex: 1;
              height: ${rpx(kEventConnectorLineHeight) +
              (model.taglines?.length ?? 0) * rpx(22) +
              rpx(16)}px;
            `}
          >
            <Tagline
              tagline={model.title}
              numberOfLines={1}
              css={`
                line-height: ${rpx(kEventConnectorLineHeight)}px;
                font-family: ${FontFamily.NotoSans.Light};
                font-size: ${rpx(13)}px;
              `}
            />
            {model.taglines?.map((tagline, i) => (
              <Tagline
                key={i}
                tagline={tagline}
                numberOfLines={1}
                css={`
                  line-height: ${rpx(18)}px;
                  font-family: ${FontFamily.NotoSans.Light};
                  font-size: ${rpx(12)}px;
                  margin-bottom: ${rpx(4)}px;
                `}
              />
            ))}
            <Text
              numberOfLines={1}
              css={`
                line-height: ${rpx(16)}px;
                margin-bottom: ${rpx(8)}px;
                font-family: ${FontFamily.NotoSans.Light};
                font-size: ${rpx(12)}px;
                color: ${Colors.Gray3};
              `}
            >
              {formatDateTime(model.timestamp)}
            </Text>
            <MediaCarousel
              display="list"
              listKey={model.id}
              numberOfItemsPerRow={4}
              itemSpacer={rpx(6)}
              medias={model.medias ?? []}
              listWidth={mediaListWidth}
              itemStyle={{
                borderRadius: rpx(5),
                overflow: 'hidden',
                marginBottom: rpx(5),
              }}
              playIconSize={rpx(16)}
              noPlaceholder={media =>
                media.type === 'image' && media.url?.includes('#')
              }
              css={`
                margin-bottom: ${rpx(10)}px;
                margin-right: ${-mediaCellSpacer}px;
                height: ${mediaListHeight}px;
              `}
            />
          </View>
        </View>
      );
    }
  },
);

export const TaskTimeline: FC<{
  taskNo: string;
  readonly?: boolean;
  isIndependentDeliveryCheckEnabled: boolean;
}> = memo(({ taskNo, readonly, isIndependentDeliveryCheckEnabled }) => {
  const { loading, data, error, refresh } = useTaskTimeline(taskNo);
  const sections = useMemo(
    () => buildTimeline(data ?? [], { isIndependentDeliveryCheckEnabled }),
    [data, isIndependentDeliveryCheckEnabled],
  );
  const { width } = useContainerLayout();

  const onPressSectionHeader = usePressTaskTimelineSectionHeader();

  const { cells } = useTaskTimelineModels({
    sections,
    onPressSectionHeader: readonly ? undefined : onPressSectionHeader,
  });

  if (!data?.length) {
    return (
      <ScrollView
        css={`
          flex: 1;
        `}
        contentContainerStyle={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!data && loading ? (
          <ActivityIndicator color={Colors.Gray2} />
        ) : !data && error ? (
          <EmptyView
            error={error}
            message={__DEV__ ? error.message : '获取工作记录失败, 请稍候重试'}
            onRetry={refresh}
          />
        ) : (
          <EmptyView message="暂无相关报告数据" onRetry={refresh} />
        )}
      </ScrollView>
    );
  }

  return (
    <FlatList
      data={cells}
      keyExtractor={x => x.id}
      initialNumToRender={20}
      showsVerticalScrollIndicator={Platform.OS === 'web'}
      renderItem={({ item }) => <Cell model={item} width={width} />}
    />
  );
});

export const TaskTimelineCell = Cell;
